// src/services/card.service.ts
import { supabase } from "./supabase";
import { CardData, UpdateCardDTO, CardStatus, CreateCardDTO } from "../types/card.types";
import { generateId } from "../utils/helpers";
import { calculatePetals } from "../utils/petals.utils";

export interface CardRow {
  id: string;
  shareable_link: string;
  sender_id: string;
  sender_email: string;
  sender_name: string;
  recipient_name: string;
  recipient_type: string;
  recipient_image_url: string | null;
  theme: string;
  message: string;
  title: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string | null;
  font_family: string;
  include_bouquet: boolean;
  bouquet_amount: number;
  status: CardStatus;
  payment_ref: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string;
  viewed_at: string | null;
  accepted_at: string | null;
  petals: number;
  is_editable: boolean;
}

class CardService {
  private readonly TABLE = "cards";
  private cache: Map<string, { data: CardData; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private handleError(error: any, message: string): never {
    console.error(`❌ ${message}:`, error);
    throw new Error(`${message}: ${error?.message || "Unknown error"}`);
  }

  async createCard(params: CreateCardDTO): Promise<CardData> {
    try {
      const now = new Date().toISOString();
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      if (!params.senderId) {
        throw new Error("User ID is required to create a card");
      }

      const petals = calculatePetals(params.customization.bouquetAmount);

      const newCard = {
        id: generateId(),
        shareable_link: await this.generateUniqueLink(),
        sender_id: params.senderId,
        sender_email: params.senderEmail,
        sender_name: params.senderName,
        recipient_name: params.customization.recipientName || "",
        recipient_type: params.customization.recipientType || "partner",
        recipient_image_url: params.customization.recipientImage || null,
        theme: params.customization.theme || "valentine",
        message: params.customization.message || "",
        title: params.customization.title || "To My Love",
        primary_color: params.customization.primaryColor || "#ff6b6b",
        secondary_color: params.customization.secondaryColor || "#feca57",
        accent_color: params.customization.accentColor || null,
        font_family: params.customization.fontFamily || "Inter",
        include_bouquet: params.customization.includeBouquet || false,
        bouquet_amount: params.customization.bouquetAmount || 0,
        status: "draft" as CardStatus,
        payment_ref: null,
        created_at: now,
        updated_at: now,
        expires_at: expiresAt,
        viewed_at: null,
        accepted_at: null,
        petals,
        is_editable: true,
      };

      const { data, error } = await supabase.from(this.TABLE).insert(newCard).select().single();

      if (error) this.handleError(error, "Failed to create card");
      return this.mapToCardData(data);
    } catch (error) {
      this.handleError(error, "Failed to create card");
    }
  }

  async getCard(id: string): Promise<CardData> {
    try {
      const cached = this.cache.get(id);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        return cached.data;
      }

      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error || !data) {
        throw new Error("Card not found");
      }

      const cardData = this.mapToCardData(data);
      this.cache.set(id, { data: cardData, timestamp: Date.now() });
      return cardData;
    } catch (error) {
      this.handleError(error, "Failed to get card");
    }
  }

  async getCardByLinkId(linkId: string): Promise<CardData> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("shareable_link", linkId)
        .maybeSingle();

      if (error || !data) {
        throw new Error("Link not found or expired");
      }

      return this.mapToCardData(data);
    } catch (error) {
      this.handleError(error, "Failed to get card by link");
    }
  }

  async markCardAsPaid(cardId: string, paymentRef: string = "free_access"): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("cards")
        .update({
          status: "paid",
          payment_ref: paymentRef,
          updated_at: new Date().toISOString(),
        })
        .eq("id", cardId);

      if (error) {
        console.error("Error marking card as paid:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in markCardAsPaid:", error);
      return false;
    }
  }

  async getUserCardsPaginated(
    userId: string,
    page: number = 0,
    limit: number = 10
  ): Promise<{ data: CardData[]; hasMore: boolean }> {
    try {
      const from = page * limit;
      const to = from + limit - 1;

      const { data, error, count } = await supabase
        .from(this.TABLE)
        .select("*", { count: "exact" })
        .eq("sender_id", userId)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) throw error;

      const hasMore = count ? from + limit < count : false;
      return {
        data: (data || []).map(this.mapToCardData),
        hasMore,
      };
    } catch (error) {
      this.handleError(error, "Failed to fetch cards");
    }
  }

  async updateCard(id: string, updates: UpdateCardDTO): Promise<CardData> {
    try {
      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      // Map updates to database fields
      const fieldMap: Record<string, string> = {
        status: "status",
        is_editable: "is_editable",
        petals: "petals",
        viewed_at: "viewed_at",
        accepted_at: "accepted_at",
        payment_ref: "payment_ref",
      };

      Object.entries(fieldMap).forEach(([key, dbKey]) => {
        if (updates[key as keyof UpdateCardDTO] !== undefined) {
          updateData[dbKey] = updates[key as keyof UpdateCardDTO];
        }
      });

      if (updates.customization) {
        const c = updates.customization;
        const customizationMap: Record<string, string> = {
          recipientImage: "recipient_image_url",
          message: "message",
          theme: "theme",
          recipientName: "recipient_name",
          senderName: "sender_name",
          bouquetAmount: "bouquet_amount",
        };

        Object.entries(customizationMap).forEach(([key, dbKey]) => {
          if (c[key as keyof typeof c] !== undefined) {
            updateData[dbKey] = c[key as keyof typeof c];
          }
        });
      }

      const { data, error } = await supabase
        .from(this.TABLE)
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      this.cache.delete(id);
      return this.mapToCardData(data);
    } catch (error) {
      this.handleError(error, "Failed to update card");
    }
  }

  async markAsReceived(id: string): Promise<CardData> {
    return this.updateCard(id, {
      status: "received",
      accepted_at: new Date().toISOString(),
      is_editable: false,
    });
  }

  async markAsOpened(id: string): Promise<CardData> {
    return this.updateCard(id, {
      status: "opened",
      viewed_at: new Date().toISOString(),
      is_editable: false,
    });
  }

  async markAsPaid(id: string, paymentRef: string): Promise<CardData> {
    return this.updateCard(id, {
      status: "paid",
      payment_ref: paymentRef,
    });
  }

  async getUserCards(userId: string): Promise<CardData[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("sender_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(this.mapToCardData);
    } catch (error) {
      this.handleError(error, "Failed to fetch user cards");
    }
  }

  private async generateUniqueLink(): Promise<string> {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      let linkId = "";
      for (let i = 0; i < 8; i++) {
        linkId += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      const { data } = await supabase
        .from(this.TABLE)
        .select("id")
        .eq("shareable_link", linkId)
        .maybeSingle();

      if (!data) return linkId;
      attempts++;
    }

    // Fallback to timestamp-based ID if all attempts fail
    return `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private mapToCardData(data: any): CardData {
    return {
      id: data.id,
      shareableLink: data.shareable_link,
      senderId: data.sender_id,
      senderEmail: data.sender_email,
      senderName: data.sender_name,
      recipientName: data.recipient_name || "",
      recipientType: data.recipient_type || "partner",
      recipientImage: data.recipient_image_url,
      theme: data.theme || "valentine",
      message: data.message || "",
      title: data.title || "To My Love",
      primaryColor: data.primary_color || "#ff6b6b",
      secondaryColor: data.secondary_color || "#feca57",
      accentColor: data.accent_color || "#ff9ff3",
      fontFamily: data.font_family || "Inter",
      includeBouquet: data.include_bouquet || false,
      bouquetAmount: data.bouquet_amount || 0,
      petals: data.petals || 0,
      includeHeartParticles: false,
      includeSparkles: false,
      status: data.status,
      paymentRef: data.payment_ref,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      expiresAt: data.expires_at,
      viewedAt: data.viewed_at,
      acceptedAt: data.accepted_at,
      isEditable: data.is_editable ?? true,
    };
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const cardService = new CardService();
